import React from "react";
import { Link } from "react-router-dom";
import { 
  GiftIcon, 
  LockIcon,
  UsersThreeIcon,
  CheckCircleIcon,
  DeviceMobileIcon,
  HeartIcon
} from "@phosphor-icons/react";
import PublicNavigation from "../components/PublicNavigation";
import PublicFooter from "../components/PublicFooter";
import { usePageMeta } from "../hooks/usePageMeta";
import "./HowItWorksPage.css";

const HowItWorksPage: React.FC = () => {

  // Aggiungi classe per rimuovere padding dal main
  React.useEffect(() => {
    document.body.classList.add("homepage-route");
    return () => {
      document.body.classList.remove("homepage-route");
    };
  }, []);

  // SEO Meta Tags
  usePageMeta({
    title: "Come funziona CosaVorrei - La lista regali privata per gruppi di famiglia e amici",
    description: "Scopri come funziona CosaVorrei: crea una cerchia di persone con cui scambiare regali tutto l'anno. Ogni evento nasce nel gruppo, con liste private e sorprese garantite.",
    ogUrl: "https://www.cosavorrei.it/come-funziona",
    ogImage: "https://www.cosavorrei.it/og-image.png",
    ogLocale: "it_IT",
    ogImageWidth: "1200",
    ogImageHeight: "630",
    twitterImage: "https://www.cosavorrei.it/og-image.png",
    twitterUrl: "https://www.cosavorrei.it/come-funziona",
    canonicalUrl: "https://www.cosavorrei.it/come-funziona",
  });

  return (
    <div className="come-funziona-page">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="come-funziona-hero">
        <h1 className="come-funziona-hero-title">
          Come funziona <span className="highlight">CosaVorrei</span>
        </h1>
        <p className="come-funziona-hero-subtitle">
          Crea una <strong>cerchia di persone</strong> con cui scambi regali tutto l'anno. <br/>
          Ogni evento nasce dentro al gruppo, e chi ne fa parte può aggiungere i propri desideri. 
          Tutto resta <strong>privato, ordinato e nello stesso posto</strong>.
        </p>
      </section>

      {/* Cosa fa CosaVorrei */}
      <section className="come-funziona-section">
        <div className="come-funziona-container">
          <h2 className="come-funziona-section-title">
            <GiftIcon size={40} weight="regular" />
            Cosa fa CosaVorrei
          </h2>
          <div className="content-text">
            <p>
              <strong>CosaVorrei è una piattaforma per gruppi che si scambiano regali:</strong> famiglie, 
              amici, colleghi. <br/>
              Dentro ogni gruppo puoi creare uno o più eventi (Natale, compleanni, 
              anniversari...) e decidere chi riceve i regali per ciascuno.<br/>
              Ogni gruppo diventa il punto di riferimento stabile per i regali tra le stesse persone, <strong>anno dopo anno.</strong>
            </p>
            <p>
              <strong>Non è una wishlist. È il luogo dove i regali prendono forma all’interno della tua cerchia.</strong>
            </p>
            <p>
              Ogni membro aggiunge i propri desideri nella lista personale e, quando arriva il momento, 
              gli altri possono prenotare i regali. <br/>
              Le prenotazioni restano segrete o parzialmente visibili, a seconda del tipo di evento:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1.5rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Evento a sorpresa:</strong> chi riceve non vede cosa è stato prenotato.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Evento visibile:</strong> chi riceve può vedere cosa è stato scelto, ma non da chi.
              </li>
            </ul>
            <p>
              <strong>Tutto è condiviso nel gruppo:</strong> niente più link sparsi, chat, o wishlist separate.
            </p>
          </div>
        </div>
      </section>

      {/* Perché è diverso */}
      <section className="come-funziona-section alt">
        <div className="come-funziona-container">
          <h2 className="come-funziona-section-title">
            <LockIcon size={40} weight="regular" />
            Perché è diverso da una wishlist pubblica
          </h2>
          <div className="content-text">
            <p>
              Le wishlist classiche sono pensate per chi vuole condividere una lista personale con tutti. <br/>
              <strong> CosaVorrei invece parte dalla relazione:</strong> è pensato per le persone che si 
              fanno regali tra loro, più volte nel tempo.
            </p>
            <p>
              <strong>Non serve ricreare o ricondividere la tua lista ogni volta.</strong> Dentro la tua 
              cerchia, ogni nuovo evento avvisa tutti, e ognuno può partecipare con la propria lista.
            </p>
            <p>
              Chi prenota un regalo non vede cosa è già stato preso, ma solo che "qualcuno ci sta pensando".<br/> 
              <strong> Così la sorpresa resta, e i doppioni spariscono.</strong>
            </p>
            <p>
              Le altre piattaforme partono dall’oggetto da regalare. <strong>CosaVorrei parte dalle persone.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Come si usa */}
      <section className="come-funziona-section">
        <div className="come-funziona-container">
          <h2 className="come-funziona-section-title">
            <UsersThreeIcon size={40} weight="regular" />
            Come si usa in famiglia o con amici
          </h2>
          <div className="content-text">
            <ol style={{ marginLeft: '2rem', marginBottom: '1.5rem', lineHeight: '1.8' }}>
              <li style={{ marginBottom: '1rem' }}>
                <strong>Crei un gruppo stabile</strong> (es. Famiglia Rossi o Amici del calcetto).
              </li>
              <li style={{ marginBottom: '1rem' }}>
                <strong>Aggiungi i membri</strong> che ricevono un link di invito.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                <strong>Crei un evento:</strong> ad esempio Natale 2025.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                <strong>Decidi chi riceve i regali</strong> (es. solo i bambini) e se è a sorpresa o visibile.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                <strong>Tutti aggiungono i loro desideri,</strong> e chi vuole fare un regalo può prenotarlo in un clic.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                Nella sezione <strong>Le mie prenotazioni</strong> vedi tutti i regali che hai deciso di fare, dove comprarli e puoi modificarli in qualsiasi momento.
              </li>
            </ol>
          </div>

          {/* Esempio pratico */}
          <div className="example-box">
            <h3 className="example-title">
              <HeartIcon size={32} weight="fill" style={{ color: '#dc3545' }} />
              Esempio pratico: Natale in famiglia
            </h3>
            <div className="example-steps">
              <div className="example-step">
                <CheckCircleIcon size={24} weight="fill" style={{ color: '#28a745' }} />
                <span>Mamma crea il gruppo "Famiglia Rossi" e invita papà, nonna, zii e figli</span>
              </div>
              <div className="example-step">
                <CheckCircleIcon size={24} weight="fill" style={{ color: '#28a745' }} />
                <span>Crea l'evento "Natale 2025" e ognuno aggiunge i propri desideri</span>
              </div>
              <div className="example-step">
                <CheckCircleIcon size={24} weight="fill" style={{ color: '#28a745' }} />
                <span>Papà consulta la lista di mamma e prenota il libro che desidera</span>
              </div>
              <div className="example-step">
                <CheckCircleIcon size={24} weight="fill" style={{ color: '#28a745' }} />
                <span>Nonna non vede più il libro che ha prenotato papà, e sceglie un altro regalo</span>
              </div>
              <div className="example-step">
                <CheckCircleIcon size={24} weight="fill" style={{ color: '#28a745' }} />
                <span>Il giorno di Natale, mamma riceve esattamente ciò che desiderava, senza doppioni</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vantaggi chiave */}
      <section className="come-funziona-section alt">
        <div className="come-funziona-container">
          <h2 className="come-funziona-section-title">Perché creare un gruppo regali su CosaVorrei</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <UsersThreeIcon size={38} weight="regular" />
              </div>
              <h3 className="benefit-title">Un solo gruppo, tutti i regali</h3>
              <p className="benefit-description">
                Tutti gli eventi e le persone in un unico spazio condiviso.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <LockIcon size={38} weight="regular" />
              </div>
              <h3 className="benefit-title">Sorpresa garantita</h3>
              <p className="benefit-description">
                Chi riceve non sa cosa arriverà, chi regala evita doppioni.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <DeviceMobileIcon size={38} weight="regular" />
              </div>
              <h3 className="benefit-title">Tutto sincronizzato</h3>
              <p className="benefit-description">
                Ogni aggiornamento è visibile in tempo reale a tutti i membri.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <CheckCircleIcon size={38} weight="regular" />
              </div>
              <h3 className="benefit-title">Flessibile e familiare</h3>
              <p className="benefit-description">
                Gestisci eventi per bambini, adulti o colleghi, con la stessa semplicità.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="come-funziona-cta">
        <div className="come-funziona-cta-container">
          <h2 className="come-funziona-cta-title">Pronto a creare la tua cerchia dei regali?</h2>
          <p className="come-funziona-cta-subtitle">
            Crea il tuo gruppo in pochi secondi e scopri quanto è facile fare regali azzeccati, senza spoiler e senza confusione.
          </p>
          <Link to="/create-group" className="come-funziona-cta-button">
            Crea il tuo gruppo
          </Link>
          <p className="come-funziona-faq-text">
            Hai domande? <Link to="/faq" className="come-funziona-faq-link-text">Leggi le FAQ</Link>
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default HowItWorksPage;
